import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCodeMerge,
  faFile,
  faFileExport,
  faPeopleGroup,
  faPowerOff,
  faUnlink,
  faUnlock,
} from '@fortawesome/pro-light-svg-icons'

import { Button, Tools } from '@domain-group/suit-theme'

import useEntityImpersonation from '@hooks/useEntityImpersonation'
import useUser from '@hooks/useUser'

import PageContext from '@contexts/pageContext'

import ScopedConditionalContent from '@components/ScopedConditionalContent'

import { confirmAction } from '@functions/entity'
import { isAdmin } from '@functions/user'
import handleCallbackAction from '@functions/handleCallbackAction'

interface UserToolsProps {
  userPayload: ReturnType<typeof useUser>,
}

const UserTools = (props: UserToolsProps) => {
  const { userPayload } = props

  const {
    callbacks: {
      exportUser: exportFn,
      manage2FASettings,
      manageAuthToken,
      manageUserTeamMemberRoles,
      unblockUser: unblockFn,
    },
    loading,
    role,
    user,
    currentUserPayload: {
      isAdmin: currentUserIsAdmin,
    },
  } = userPayload

  const pageContext = useContext(PageContext)
  const {
    callbacks: { exportUser },
  } = pageContext

  // @ts-expect-error TS2339
  const userIsAdmin = isAdmin(role.title)

  const unblockUserPayload = {
    callbacks: {
      action: unblockFn,
    },
    toastText: 'User Unblocked',
  }

  const exportUserPayload = {
    callbacks: {
      action: exportFn,
    },
    toastText: 'Export User Queued',
  }

  const { callbacks: { unlinkImpersonation } } = useEntityImpersonation()
  const { callbacks: { softDeleteUser, undeleteUser } } = useUser(user)

  const unlinkImpersonationPayload = {
    callbacks: {
      actionFn: unlinkImpersonation,
    },
    options: {
      icon: 'warning',
      entityParams: { userId: user?.id, skipRedirect: true },
    },
    strings: {
      actionString: 'unlink',
      entityName: 'User impersonation',
    },
  }

  const deleteUserPayload = {
    callbacks: {
      actionFn: user.deleted ? undeleteUser : softDeleteUser,
    },
    options: {
      icon: 'warning',
    },
    strings: {
      actionString: user.deleted ? 'Reactivate' : 'Deactivate',
      entityName: 'User',
    },
  }

  return (
    <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
      <Tools>
        <Button
          buttonStyle="secondaryUtility"
          icon={<FontAwesomeIcon icon={faCodeMerge} />}
          onClick={() => manage2FASettings()}
          size="medium"
        >
          2FA Settings
        </Button>

        <Button
          buttonStyle="secondaryUtility"
          icon={<FontAwesomeIcon icon={faFile} />}
          //@ts-expect-error
          marginTop="medium"
          onClick={() => manageAuthToken()}
          size="medium"
        >
          Authentication Token
        </Button>

        {userIsAdmin && (
          <Button
            buttonStyle="secondaryUtility"
            icon={<FontAwesomeIcon icon={faPeopleGroup} />}
            loading={loading}
            //@ts-expect-error
            marginTop="medium"
            onClick={() => manageUserTeamMemberRoles()}
            size="medium"
          >
            Manage Team Member Roles
          </Button>
        )}

        <Button
          buttonStyle="secondaryUtility"
          loading={loading}
          icon={<FontAwesomeIcon icon={faFileExport} />}
          //@ts-expect-error
          marginTop="medium"
          onClick={() => exportUser(exportUserPayload)}
          size="medium"
        >
          Export User
        </Button>

        <Button
          buttonStyle="secondaryUtility"
          icon={<FontAwesomeIcon icon={faUnlock} />}
          //@ts-expect-error
          marginTop="medium"
          onClick={() => handleCallbackAction(unblockUserPayload)}
          size="medium"
        >
          Unblock User
        </Button>

        {!!user?.impersonating_with_token && (
          <Button
            buttonStyle="secondaryUtility"
            loading={loading}
            icon={<FontAwesomeIcon icon={faUnlink} />}
            //@ts-expect-error
            marginTop="medium"
            // @ts-expect-error TS2345
            onClick={() => confirmAction(unlinkImpersonationPayload)}
            size="medium"
          >
            Unlink Impersonation
          </Button>
        )}

        {currentUserIsAdmin && (
          <Button
            buttonStyle="secondaryUtility"
            loading={loading}
            icon={<FontAwesomeIcon icon={faPowerOff} />}
            //@ts-expect-error
            marginTop="medium"
            // @ts-expect-error TS2345
            onClick={() => confirmAction(deleteUserPayload)}
            size="medium"
          >
            {user?.deleted ? 'Reactivate' : 'Deactivate'} User
          </Button>
        )}
      </Tools>
    </ScopedConditionalContent>
  )
}

export default UserTools
